import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Clock, MapPin, Search, ArrowRight, Loader2, X, Leaf, Heart, Sparkles, ShieldCheck, Check, Gift } from "lucide-react";
import AIConciergeWidget from "@/components/AIConciergeWidget";
import DemoTopNav from "@/components/demos/DemoTopNav";
import { DEMO_THEMES } from "@/data/demoThemes";
import { conciergeBook } from "@/lib/api";
import { toast } from "sonner";

const SAGE = "#5E6F55";
const DEEP = "#33372C";
const CREAM = "#F6F4EF";
const serif = { fontFamily: "'Playfair Display', serif" };
const body = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const IMG = {
  hero: "https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?crop=entropy&cs=srgb&fm=jpg&w=1100&q=80&ixlib=rb-4.1.0",
  t1: "https://images.unsplash.com/photo-1519823551278-64ac92734fb1?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0",
  t2: "https://images.unsplash.com/photo-1741522509438-a120c0bb5e88?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0",
  t3: "https://images.unsplash.com/photo-1639162906614-0603b0ae95fd?crop=entropy&cs=srgb&fm=jpg&w=500&q=80&ixlib=rb-4.1.0",
  ritual: "https://images.unsplash.com/photo-1636714455535-7cb8e8b34790?crop=entropy&cs=srgb&fm=jpg&w=800&q=80&ixlib=rb-4.1.0",
  p1: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=300&q=80&ixlib=rb-4.1.0",
  p2: "https://images.unsplash.com/photo-1600878459138-e1123b37cb30?crop=entropy&cs=srgb&fm=jpg&w=300&q=80&ixlib=rb-4.1.0",
  p3: "https://images.unsplash.com/photo-1609436132311-e4b0c9370469?crop=entropy&cs=srgb&fm=jpg&w=300&q=80&ixlib=rb-4.1.0",
  p4: "https://images.pexels.com/photos/37148308/pexels-photo-37148308.jpeg?auto=compress&cs=tinysrgb&w=300&q=80",
};
const TREATMENTS = [
  { img: IMG.t1, name: "Swedish Massage", dur: "60 min", price: "$110" },
  { img: IMG.t2, name: "Deep Tissue", dur: "60 min", price: "$120" },
  { img: IMG.ritual, name: "Hot Stone", dur: "75 min", price: "$135" },
  { img: IMG.t3, name: "Prenatal Massage", dur: "60 min", price: "$115" },
  { img: IMG.t1, name: "Couples Massage", dur: "60 min", price: "$220" },
];
const THERAPISTS = [
  { img: IMG.p1, name: "Jasmine", role: "LMT" },
  { img: IMG.p2, name: "Marcus", role: "LMT" },
  { img: IMG.p3, name: "Elena", role: "LMT" },
  { img: IMG.p4, name: "Daniel", role: "LMT" },
];
const DIFF = [
  { icon: ShieldCheck, t: "Licensed massage therapists" },
  { icon: Heart, t: "A welcoming, inclusive space" },
  { icon: Leaf, t: "Natural, premium products" },
  { icon: Sparkles, t: "Serene locations" },
];
const Cap = ({ children }) => <span className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: SAGE }}>{children}</span>;

function BookingModal({ preset, onClose }) {
  const [form, setForm] = useState({ treatment: preset || "Massage (Any)", therapist: "Any Therapist", date: "", time: "", name: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!form.name.trim() || !form.contact.trim() || !form.date || !form.time) {
      setErr("Please fill in your name, contact, date and time."); return;
    }
    const isEmail = form.contact.includes("@");
    setBusy(true);
    try {
      const res = await conciergeBook({
        demo: "spa", session_id: `spa-web-${Date.now()}`, name: form.name.trim(),
        email: isEmail ? form.contact.trim() : null, phone: isEmail ? null : form.contact.trim(),
        date: form.date, time: form.time, service: `${form.treatment} · ${form.therapist}`,
        notes: null,
      });
      setDone(res.confirmation);
      toast.success("Appointment requested");
    } catch (e) { setErr(e?.response?.data?.detail || "Something went wrong."); }
    finally { setBusy(false); }
  };

  const field = "w-full rounded-lg px-3 py-2.5 text-sm outline-none border bg-white";
  return (
    <div className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" onClick={onClose} data-testid="spa-booking-modal">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} style={body}>
        <div className="px-6 py-4 flex items-center justify-between" style={{ background: SAGE, color: "#fff" }}>
          <div style={serif} className="text-lg">Book a massage</div>
          <button onClick={onClose} data-testid="spa-booking-close"><X size={18} /></button>
        </div>
        {done ? (
          <div className="p-8 text-center" data-testid="spa-booking-success">
            <div className="w-12 h-12 rounded-full mx-auto flex items-center justify-center" style={{ background: `${SAGE}22`, color: SAGE }}><Check size={22} /></div>
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">{done}</p>
            <button onClick={onClose} className="mt-6 rounded-lg px-6 py-2.5 text-sm font-semibold text-white" style={{ background: SAGE }}>Done</button>
          </div>
        ) : (
          <div className="p-6 space-y-3" style={{ borderColor: "#e5e2d8" }}>
            <select value={form.treatment} onChange={(e) => setForm({ ...form, treatment: e.target.value })} className={field} style={{ borderColor: "#e5e2d8" }} data-testid="spa-booking-treatment">
              {["Massage (Any)", "Swedish Massage", "Deep Tissue", "Hot Stone", "Prenatal Massage", "Couples Massage", "The Stillwater Ritual"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <select value={form.therapist} onChange={(e) => setForm({ ...form, therapist: e.target.value })} className={field} style={{ borderColor: "#e5e2d8" }} data-testid="spa-booking-therapist">
              {["Any Therapist", "Jasmine", "Marcus", "Elena", "Daniel"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <div className="flex gap-3">
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={`${field} w-1/2`} style={{ borderColor: "#e5e2d8" }} data-testid="spa-booking-date" />
              <input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={`${field} w-1/2`} style={{ borderColor: "#e5e2d8" }} data-testid="spa-booking-time" />
            </div>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={field} style={{ borderColor: "#e5e2d8" }} data-testid="spa-booking-name" />
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="Email or phone" className={field} style={{ borderColor: "#e5e2d8" }} data-testid="spa-booking-contact" />
            {err && <div className="text-xs text-red-500" data-testid="spa-booking-error">{err}</div>}
            <button onClick={submit} disabled={busy} data-testid="spa-booking-submit"
              className="w-full rounded-lg px-4 py-3 text-sm font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: SAGE }}>
              {busy ? <Loader2 size={16} className="animate-spin" /> : null} Request appointment
            </button>
            <p className="text-[11px] text-slate-400 text-center flex items-center justify-center gap-1"><ShieldCheck size={12} /> Secure &amp; private booking</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}

export default function StillwaterDemo() {
  const [booking, setBooking] = useState(null); // null | {preset}
  useEffect(() => { window.scrollTo(0, 0); document.title = "Stillwater Spa & Massage (Demo) | WebsiteTech.pro"; }, []);
  const openBooking = (preset) => setBooking({ preset });

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: DEEP, ...body }} data-testid="spa-demo">
      <Link to="/demos" className="block w-full text-center text-[11px] tracking-widest uppercase py-1.5 text-white" style={{ background: SAGE }}>
        WebsiteTech.pro Demo · Spa &amp; Wellness — click to view all industry demos
      </Link>
      <div className="hidden sm:block text-xs" style={{ background: "#EDEAE1", color: DEEP }}>
        <div className="max-w-7xl mx-auto px-6 h-9 flex items-center justify-between">
          <span className="flex items-center gap-4 opacity-80">
            <span className="flex items-center gap-1.5"><Phone size={12} /> (555) 248-7729</span>
            <span className="flex items-center gap-1.5"><Clock size={12} /> Open Daily 9AM – 8PM</span>
            <span className="flex items-center gap-1.5"><MapPin size={12} /> 123 Willow Creek Drive, Portland, OR</span>
          </span>
          <span className="flex items-center gap-4 opacity-80"><span className="cursor-pointer hover:opacity-100">Gift Cards</span><span className="cursor-pointer">Accessibility</span><span className="cursor-pointer">Cancellation Policy</span><Search size={13} /></span>
        </div>
      </div>

      <DemoTopNav
        testid="spa-nav"
        logo={<div className="leading-tight flex items-center gap-2"><Leaf size={18} style={{ color: SAGE }} /><div><div className="text-base tracking-[0.12em]" style={{ ...serif, color: DEEP }}>STILLWATER</div><div className="text-[9px] tracking-[0.35em] uppercase" style={{ color: SAGE }}>Spa &amp; Massage</div></div></div>}
        items={["Massage", "Body Treatments", "Facials", "Packages", "Memberships", "Gift Cards", "About"]}
        ctaLabel="Book a Massage"
        onCta={() => openBooking()}
        theme={{ bg: "rgba(246,244,239,0.92)", text: DEEP, accent: SAGE, accentText: "#fff", border: "#e5e2d8" }}
      />

      {/* hero */}
      <header data-testid="spa-hero" className="relative">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-8 items-center pt-14 pb-24 md:pb-28">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" style={serif}>Return to your<br />natural rhythm.</h1>
            <p className="mt-6 text-lg opacity-75 max-w-md">Personalized massage and restorative rituals for body and mind.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => openBooking()} data-testid="spa-book-hero" className="rounded-full px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white flex items-center gap-2" style={{ background: SAGE }}>Book a Massage <ArrowRight size={14} /></button>
              <button className="rounded-full px-7 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold" style={{ border: `1px solid ${SAGE}55`, color: DEEP }}>Explore Treatments</button>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }} className="relative">
            <img src={IMG.hero} alt="Massage therapy" className="rounded-2xl h-[380px] w-full object-cover shadow-xl" />
            <div className="absolute top-5 right-5 text-right text-white text-xs tracking-[0.3em] uppercase" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}>Wellness<br />Lives Here</div>
          </motion.div>
        </div>

        {/* booking bar */}
        <div className="max-w-6xl mx-auto px-6 -mt-12 relative z-10">
          <div className="bg-white rounded-2xl shadow-xl border p-4 flex flex-col lg:flex-row gap-3 items-stretch lg:items-center" style={{ borderColor: "#e5e2d8" }} data-testid="spa-booking-bar">
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Treatment</div><div className="text-sm font-medium">Massage (Any)</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Location</div><div className="text-sm font-medium">Portland (Main Spa)</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Date</div><div className="text-sm font-medium">Choose a date</div></div>
              <div><div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Therapist</div><div className="text-sm font-medium">Any Therapist</div></div>
            </div>
            <button onClick={() => openBooking()} data-testid="spa-find-appointment" className="rounded-full px-8 py-3.5 text-xs tracking-[0.18em] uppercase font-semibold text-white whitespace-nowrap" style={{ background: SAGE }}>Find an Appointment</button>
          </div>
        </div>
      </header>

      {/* treatments */}
      <section data-testid="spa-treatments" className="max-w-7xl mx-auto px-6 py-20">
        <Cap>Massage Therapies</Cap>
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {TREATMENTS.map((t, i) => (
            <motion.button key={t.name} onClick={() => openBooking(t.name)} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 5) * 0.06, duration: 0.4 }}
              className="group text-left bg-white rounded-xl overflow-hidden border hover:shadow-lg transition-shadow" style={{ borderColor: "#e5e2d8" }} data-testid={`spa-treatment-${i}`}>
              <img src={t.img} alt={t.name} className="h-32 w-full object-cover" />
              <div className="p-3.5">
                <div className="font-semibold text-sm">{t.name}</div>
                <div className="text-xs text-slate-400 mt-0.5">{t.dur} · <span style={{ color: SAGE }} className="font-semibold">{t.price}</span></div>
              </div>
            </motion.button>
          ))}
          <div className="col-span-2 md:col-span-3 lg:col-span-5 relative rounded-xl overflow-hidden mt-2" style={{ minHeight: 120 }}>
            <img src={IMG.ritual} alt="The Stillwater Ritual" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(51,55,44,0.85), rgba(94,111,85,0.55))" }} />
            <div className="relative p-8 text-white flex flex-wrap items-center justify-between gap-4">
              <div><div className="text-xs tracking-[0.25em] uppercase opacity-80">The Stillwater Ritual</div><div className="mt-1 text-2xl" style={serif}>90 minutes of customized massage & aromatherapy</div></div>
              <button onClick={() => openBooking("The Stillwater Ritual")} className="rounded-full px-6 py-3 text-xs tracking-widest uppercase font-semibold bg-white" style={{ color: SAGE }}>90 min · $165</button>
            </div>
          </div>
        </div>
      </section>

      {/* therapists + membership + gift + first visit */}
      <section className="border-y py-20" style={{ background: "#EFEDE4", borderColor: "#e5e2d8" }}>
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
          <div data-testid="spa-therapists">
            <Cap>Meet Our Therapists</Cap>
            <div className="mt-6 grid grid-cols-4 gap-4">
              {THERAPISTS.map((p) => (
                <div key={p.name} className="text-center">
                  <img src={p.img} alt={p.name} className="h-24 w-24 rounded-full object-cover mx-auto" />
                  <div className="mt-2 font-semibold text-sm">{p.name}</div>
                  <div className="text-[10px] tracking-widest uppercase" style={{ color: SAGE }}>{p.role}</div>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <Cap>Wellness Membership</Cap>
              <p className="mt-3 text-sm opacity-70">Make restoration part of your routine.</p>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                {["Member pricing", "Exclusive perks", "Flexible options", "A more balanced you"].map((m) => (
                  <div key={m} className="flex items-center gap-2"><Check size={15} style={{ color: SAGE }} /> {m}</div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 border flex flex-col" style={{ borderColor: "#e5e2d8" }} data-testid="spa-giftcards">
              <Gift size={22} style={{ color: SAGE }} />
              <div className="mt-3" style={serif}><span className="text-xl">Give the Gift of Wellness</span></div>
              <p className="mt-2 text-sm opacity-70 flex-1">A thoughtful way to share calm with someone you love.</p>
              <button className="mt-4 rounded-full px-5 py-2.5 text-xs tracking-widest uppercase font-semibold text-white self-start" style={{ background: SAGE }}>Shop Gift Cards</button>
            </div>
            <div className="bg-white rounded-2xl p-6 border" style={{ borderColor: "#e5e2d8" }} data-testid="spa-firstvisit">
              <div style={serif} className="text-xl">Your First Visit</div>
              <div className="mt-3 space-y-2 text-sm">
                {["What to expect", "How to prepare", "Your comfort comes first", "A welcoming, inclusive space"].map((f) => (
                  <div key={f} className="flex items-center gap-2"><Check size={15} style={{ color: SAGE }} /> {f}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* testimonial + difference */}
      <section className="max-w-7xl mx-auto px-6 py-16 text-center">
        <p className="text-2xl md:text-3xl max-w-3xl mx-auto leading-relaxed" style={serif}>"Stillwater is my happy place. The staff are kind, professional, and truly listen. Every visit leaves me feeling renewed."</p>
        <div className="mt-4 text-sm text-slate-500">★★★★★ &nbsp; — Taylor M.</div>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 border-t pt-10" style={{ borderColor: "#e5e2d8" }}>
          {DIFF.map((d) => (
            <div key={d.t} className="flex flex-col items-center gap-2 text-center">
              <d.icon size={22} style={{ color: SAGE }} />
              <span className="text-sm opacity-75 max-w-[160px]">{d.t}</span>
            </div>
          ))}
        </div>
      </section>

      <footer data-testid="spa-footer" className="text-white" style={{ background: DEEP }}>
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-wrap items-center justify-between gap-6 text-sm">
          <div className="flex items-center gap-2"><Leaf size={18} style={{ color: "#B7C4A8" }} /><div><div className="tracking-[0.12em]" style={serif}>STILLWATER</div><div className="text-[9px] tracking-[0.35em] uppercase opacity-70">Spa &amp; Massage</div></div></div>
          <p className="opacity-70">Massage · Body Treatments · Facials · Packages · Memberships · Gift Cards</p>
          <p className="opacity-70">A calmer, kinder, brighter you.</p>
        </div>
      </footer>

      {booking && <BookingModal preset={booking.preset} onClose={() => setBooking(null)} />}
      <AIConciergeWidget demo="spa" concierge={DEMO_THEMES.spa.concierge} />
    </div>
  );
}
